/**
 * EmployeeDetailPage — Story 106-7 (E-106 HR UX Max)
 *
 * Read-view activity summary:
 *  - Bal oldal: meglévő 8-mező adat grid (változatlan)
 *  - Jobb oldal: 3 mini-card (havi jelenlét, leave balance, utolsó 3 rekord)
 *  - Szabadság előzmények link
 *
 * Mobile: stack 1-col <md, 2-col ≥md.
 */

import { useTranslation } from '@kgc/i18n';
import { Button, Card, CardContent, CardHeader, CardTitle, toast } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CreateEmployeeInput } from '../api/hr-api';
import { EmployeeProfileForm } from '../components/EmployeeProfileForm';
import { useAttendance } from '../hooks/useAttendance';
import { useCreateEmployee } from '../hooks/useCreateEmployee';
import { useEmployee } from '../hooks/useEmployee';
import { useLeaveBalance } from '../hooks/useLeaveBalance';
import { useUpdateEmployee } from '../hooks/useUpdateEmployee';

function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${m}m`;
}

export function EmployeeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(id === 'new');

  const isNew = id === 'new';

  const { data: employee, isLoading, error } = useEmployee(isNew ? undefined : id);
  const updateEmployee = useUpdateEmployee();
  const createEmployee = useCreateEmployee();

  // 106-7: activity data (csak ha NEM új employee)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const { data: attendanceRecords = [] } = useAttendance(currentYear, currentMonth);
  const { data: balances = [] } = useLeaveBalance(isNew ? undefined : id, currentYear);

  // Havi jelenlét aggregátum az adott employee-re
  const attendanceSummary = useMemo(() => {
    if (isNew || !id) return null;
    const empRecords = attendanceRecords.filter(r => r.employeeProfileId === id);
    let workedDays = 0;
    let overtimeMinutes = 0;
    let leaveDays = 0;
    let missingDays = 0;

    for (const rec of empRecords) {
      if (rec.isHoliday) {
        leaveDays++;
        continue;
      }
      if (rec.actualStart) {
        workedDays++;
        overtimeMinutes += rec.overtimeMinutes;
      } else if (!rec.isWeekend) {
        missingDays++;
      }
    }

    return { workedDays, overtimeMinutes, leaveDays, missingDays, total: empRecords.length };
  }, [attendanceRecords, id, isNew]);

  // Utolsó 3 rekord reverse-chronological
  const recentRecords = useMemo(() => {
    if (isNew || !id) return [];
    return attendanceRecords
      .filter(r => r.employeeProfileId === id && r.actualStart)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  }, [attendanceRecords, id, isNew]);

  const handleSubmit = async (data: CreateEmployeeInput) => {
    try {
      if (isNew) {
        const created = await createEmployee.mutateAsync(data);
        toast.success(t('hr.employee.created'));
        navigate(`/hr/employees/${created.id}`);
      } else if (id) {
        await updateEmployee.mutateAsync({ id, data });
        toast.success(t('hr.employee.updated'));
        setIsEditing(false);
      }
    } catch {
      toast.error(t('hr.employee.saveError'));
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (!isNew && error) {
    return (
      <div className="p-6">
        <p className="text-destructive">{t('common.error')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/hr/employees')}>
            ← {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold">
            {isNew ? t('hr.employee.create') : (employee?.userName ?? employee?.userId ?? '…')}
          </h1>
        </div>
        {!isNew && !isEditing && (
          <Button onClick={() => setIsEditing(true)}>{t('common.edit')}</Button>
        )}
        {!isNew && isEditing && (
          <Button variant="outline" onClick={() => setIsEditing(false)}>
            {t('common.cancel')}
          </Button>
        )}
      </div>

      {isNew || isEditing ? (
        <EmployeeProfileForm
          employee={employee}
          onSubmit={data => void handleSubmit(data)}
          isLoading={createEmployee.isPending || updateEmployee.isPending}
        />
      ) : (
        employee && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Bal oldal: adat grid (col-span 2) */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t('hr.employee.basicInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.employmentType')}</p>
                  <p className="font-medium">{employee.employmentType}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.grossSalary')}</p>
                  <p className="font-medium">{employee.grossSalary.toLocaleString('hu-HU')} Ft</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.weeklyHours')}</p>
                  <p className="font-medium">{employee.weeklyHours} h</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.contractStart')}</p>
                  <p className="font-medium">
                    {new Date(employee.contractStart).toLocaleDateString('hu-HU')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.tajNumber')}</p>
                  <p className="font-medium">{employee.tajNumber ?? '–'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.taxId')}</p>
                  <p className="font-medium">{employee.taxId ?? '–'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.bankAccount')}</p>
                  <p className="font-medium">{employee.bankAccountNumber ?? '–'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('hr.employee.childrenCount')}</p>
                  <p className="font-medium">{employee.childrenCount}</p>
                </div>
              </CardContent>
            </Card>

            {/* Jobb oldal: 3 mini-card */}
            <div className="space-y-4">
              {/* Mini-card 1: Havi jelenlét */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {t('hr.employee.miniCardAttendance', {
                      year: currentYear,
                      month: currentMonth,
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {attendanceSummary === null || attendanceSummary.total === 0 ? (
                    <p className="text-muted-foreground">{t('hr.employee.noAttendanceData')}</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t('hr.attendance.workedDays')}
                        </span>
                        <span className="font-medium">{attendanceSummary.workedDays}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t('hr.attendance.overtimeLabel')}
                        </span>
                        <span className="font-medium">
                          {formatMinutes(attendanceSummary.overtimeMinutes)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('hr.leave.title')}</span>
                        <span className="font-medium">{attendanceSummary.leaveDays}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t('hr.employee.missingDays')}
                        </span>
                        <span
                          className={`font-medium ${
                            attendanceSummary.missingDays > 0
                              ? 'text-amber-600 dark:text-amber-400'
                              : ''
                          }`}
                        >
                          {attendanceSummary.missingDays}
                        </span>
                      </div>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => navigate('/hr/attendance')}
                  >
                    {t('hr.employee.viewFullAttendance')} →
                  </Button>
                </CardContent>
              </Card>

              {/* Mini-card 2: Leave balance */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {t('hr.employee.miniCardLeaveBalance', { year: currentYear })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {balances.length === 0 ? (
                    <p className="text-muted-foreground">{t('hr.employee.noLeaveBalance')}</p>
                  ) : (
                    balances.map(bal => {
                      const pct =
                        bal.entitled > 0
                          ? Math.max(0, Math.min(100, (bal.remaining / bal.entitled) * 100))
                          : 0;
                      const depleted = bal.remaining <= 0;
                      return (
                        <div key={bal.id} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                              {bal.leaveTypeId.slice(0, 8)}…
                            </span>
                            <span
                              className={`font-medium ${depleted ? 'text-red-600 dark:text-red-400' : ''}`}
                            >
                              {bal.remaining} / {bal.entitled}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                            <div
                              className={`h-full ${depleted ? 'bg-red-500' : 'bg-primary'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => navigate('/hr/leave')}
                  >
                    {t('hr.employee.viewLeaveHistory')} →
                  </Button>
                </CardContent>
              </Card>

              {/* Mini-card 3: Utolsó 3 rekord */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {t('hr.employee.miniCardRecentRecords')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {recentRecords.length === 0 ? (
                    <p className="text-muted-foreground">{t('hr.employee.noRecentRecords')}</p>
                  ) : (
                    recentRecords.map(rec => (
                      <div
                        key={rec.id}
                        className="flex items-center justify-between border-b pb-2 last:border-b-0"
                      >
                        <span className="text-xs text-muted-foreground">
                          {new Date(rec.date).toLocaleDateString('hu-HU')}
                        </span>
                        <span className="font-medium text-xs">
                          {rec.actualStart}
                          {rec.actualEnd ? ` – ${rec.actualEnd}` : ''}
                        </span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )
      )}
    </div>
  );
}
